import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../lib/utils'

// 4 variants × 3 sizes — that's the whole set. Don't add color/size overrides via className.
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'icon'

const baseClasses = 'inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed'

// Gradient system: brand = blue-500 → purple-600, danger = red-500 → rose-600. No solid color fills.
const variantClasses = {
  primary: 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-sm shadow-purple-500/20', // the main action — one per view
  secondary: 'bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-primary-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:hover:from-blue-900/70 dark:hover:to-purple-900/70 dark:text-blue-200', // everything else: Cancel, Export, Edit — soft brand gradient, never white
  ghost: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20', // icon actions, close, menus
  danger: 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-sm shadow-rose-500/20' // destructive: Delete, Clear, Stop
}

// Every button is the same height (h-9); sizes only change horizontal padding.
const sizeClasses = {
  sm: 'h-9 px-3 text-sm rounded-lg',   // toolbars, card headers, table rows
  md: 'h-9 px-4 text-sm rounded-lg',   // forms, modals (default)
  icon: 'h-9 w-9 p-0 rounded-lg'       // icon-only; always give aria-label/title
}

/** Button look for elements that can't be <Button> (e.g. a plain <button>, a <label>, a link). */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string) {
  return cn(baseClasses, variantClasses[variant], sizeClasses[size], className)
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, disabled, children, ...props }, ref) => {
    return (
      <motion.button
        ref={ref}
        className={buttonClass(variant, size, className)}
        disabled={disabled || loading}
        {...(props as any)}
      >
        {loading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </motion.button>
    )
  }
)

Button.displayName = 'Button'

export default Button